import React, { useState } from 'react'
import PropTypes from 'prop-types'

import cx from 'classnames'
import moment from 'moment'

import 'react-dates/initialize'
import { DayPickerRangeController } from 'react-dates'
import 'react-dates/lib/css/_datepicker.css'
import '../../assets/stylesheets/pages/cart/react-dates-overrides.scss'
import { DateRangePickerPhrases } from 'react-dates/lib/defaultPhrases'

import styles from '../../assets/stylesheets/pages/cart/schedule.module.scss'

import { TextInput } from 'lensrentals-components'
import RentalPlan, { RentalDate } from '../helpers/rental_plan'

// constants from react-dates package
const START_DATE = 'startDate'
const END_DATE = 'endDate'

const isRentalDate = date => date instanceof RentalDate

// DatePickerManager is a container that contains testable logic for <DatePicker />
// this logic is complicated because this component needs to take server data as input from props,
// manage user interactions in state and puppeteer two external libraries,
// then rectify user actions into a single endpoint and post that back to the server with `updateCartPlan`

const DatePickerManager = props => {
  const {
    updateCartPlan,
    cartPlan
  } = props

  const [showCalendar, setShowCalender] = useState(false)
  const [focusedInput, setFocusedInput] = useState(START_DATE)
  const [clientDates, setClientDates] = useState({
    startDate: null,
    endDate: null
  })

  // input methods
  const onInputClick = () => setShowCalender(true)

  // when the user starts interacting with an input, changes need to be stored in component state...
  const onInputFocus = event => {
    const { name, value } = event.target
    // notify the calendar widget which date a user is changing so we can update it accordingly
    setFocusedInput(name)
    setClientDates(previousDates => ({ ...previousDates, [name]: value }))
  }

  // ...store the changes as the user makes them...
  const onInputChange = event => {
    const { name, value } = event.target
    setClientDates(previousDates => ({ ...previousDates, [name]: value }))
  }

  // ...and post back to the server when they are done
  const onInputBlur = event => {
    const { name } = event.target
    if (name === END_DATE) {
      updateCartPlan(cartPlan.withNewDates({ newReturn: clientDates.endDate }))
    } else { // START_DATE
      updateCartPlan(cartPlan.withNewDates({ newArrival: clientDates.startDate }))
    }

    // cleanup clientDates
    setClientDates(previousDates => ({ ...previousDates, [name]: null }))
  }

  // calendar methods
  const onDatesChange = data => {
    const stripUtc = date => moment(date.format('YYYY-MM-DD'))

    // data.endDate will be null if user is trying to set end date prior to current start date
    // set the past date to the start date, let them choose a new end date
    if (focusedInput === END_DATE && data.endDate === null) {
      setClientDates({
        startDate: new RentalDate(stripUtc(data.startDate)),
        endDate: null
      })

    // if a user sets an end date, assume they are done and post both dates to the server
    } else if (focusedInput === END_DATE) {
      updateCartPlan(
        cartPlan.withNewDates({
          newArrival: isRentalDate(clientDates.startDate) ? clientDates.startDate.serverDateString : new RentalDate(stripUtc(data.startDate)).serverDateString,
          newReturn: new RentalDate(stripUtc(data.endDate)).serverDateString
        })
      )

      setShowCalender(false)
      setFocusedInput(START_DATE)
      // cleanup clientDates
      setClientDates({
        startDate: null,
        endDate: null
      })

      // if a user sets an end date, hold off on posting to the server unless they choose an endDate
    } else { // START_DATE
      setFocusedInput(END_DATE)
      setClientDates({
        startDate: new RentalDate(stripUtc(data.startDate)),
        endDate: null
      })
    }
  }

  // post the users progress to the server whenever they close the calendar
  const handleOutsideClick = () => {
    if (showCalendar) {
      updateCartPlan(
        cartPlan.withNewDates({
          newArrival: isRentalDate(clientDates.startDate) ? clientDates.startDate.serverDateString : null,
          newReturn: isRentalDate(clientDates.endDate) ? clientDates.endDate.serverDateString : null
        })
      )
      setClientDates({ startDate: null, endDate: null })
      setShowCalender(false)
    }
  }

  // return a Presenter Component so we can intercept these props during testing
  return (
    <DatePickerRenderer
      arrivalDate={cartPlan.arrivalDate}
      earliestArrivalDate={cartPlan.earliestArrivalDate}
      focusedInput={focusedInput}
      setFocusedInput={setFocusedInput}
      serverDates={{
        startDate: new RentalDate(cartPlan.arrivalDate),
        endDate: new RentalDate(cartPlan.returnDate)
      }}
      clientDates={clientDates}
      inputFunctions={{
        onClick: onInputClick,
        onChange: onInputChange,
        onFocus: onInputFocus,
        onBlur: onInputBlur,
        onKeyPress: (e) => { e.key === 'Enter' && e.preventDefault() }
      }}
      showCalendar={showCalendar}
      handleOutsideClick={handleOutsideClick}
      onDatesChange={onDatesChange}
    />
  )
}

DatePickerManager.propTypes = {
  updateCartPlan: PropTypes.func.isRequired,
  cartPlan: PropTypes.instanceOf(RentalPlan).isRequired
}

const DatePickerRenderer = props => {
  const {
    earliestArrivalDate,
    focusedInput,
    setFocusedInput,
    serverDates,
    clientDates,
    inputFunctions,
    showCalendar,
    handleOutsideClick,
    onDatesChange
  } = props

  let earliestArrivalDateRentalDate = earliestArrivalDate
  if (typeof earliestArrivalDate === 'string') {
    earliestArrivalDateRentalDate = new RentalDate(earliestArrivalDate)
  }

  // dates can be passed into the component in various forms

  // the input should show a string if the user is typing, a RentalDate's string from state, or props, in that order of precedence
  const getInputValue = (input, format = 'clientDateString') => {
    // if the input in state is a `RentalDate`
    if (clientDates[input] && isRentalDate(clientDates[input])) {
      return clientDates[input][format]

    // if the input value in state is a string, allow it to persist if a user has deleted entire string
    } else if (clientDates[input] || clientDates[input] === '') {
      return clientDates[input]

    // if there is not input in state, return from props
    } else {
      return serverDates[input][format]
    }
  }

  // the calendar requires a moment object
  // if there is one in state the user is interacting with the calendar, otherwise get the one from props
  const getCalendarValue = input => {
    return isRentalDate(clientDates[input]) ? clientDates[input].momentObj : serverDates[input].momentObj
  }

  // block out days in the past, weekend, holidays
  const isDayBlocked = day => {
    const rentalDate = new RentalDate(day)
    return (rentalDate.momentObj.isBefore(earliestArrivalDateRentalDate.momentObj) || !rentalDate.isBusinessDay)
  }
  return (
    <div className={styles['schedule-date-selection']}>
      <div className={styles['schedule-date-selection__inputs']}>
        <TextInput
          className={cx(
            'arrive',
            styles['schedule-date-selection__input'],
            styles['schedule-date-selection__input--start'],
            showCalendar && (focusedInput === START_DATE) && styles['schedule-date-selection__input--active']
          )}
          name={START_DATE}
          id={START_DATE}
          placeholder='Start'
          label='Start Date'
          value={getInputValue(START_DATE)}
          {...inputFunctions}
        />

        <TextInput
          className={cx(
            'return',
            styles['schedule-date-selection__input'],
            styles['schedule-date-selection__input--end'],
            showCalendar && (focusedInput === END_DATE) && styles['schedule-date-selection__input--active']
          )}
          name={END_DATE}
          id={END_DATE}
          placeholder='End'
          label='End Date'
          value={getInputValue(END_DATE)}
          {...inputFunctions}
        />
      </div>
      <div className={cx(
        'schedule-date-selection__calendar', // intentionally global classname to get picked up by react-dates.scss
        styles['schedule-date-selection__calendar'],
        showCalendar && styles['schedule-date-selection__calendar--open']
      )}
      >
        <DayPickerRangeController
          onDatesChange={onDatesChange}
          onFocusChange={newFocus => setFocusedInput(newFocus)}
          startDate={getCalendarValue(START_DATE)}
          endDate={getCalendarValue(END_DATE)}
          focusedInput={focusedInput}
          initialVisibleMonth={() => getCalendarValue(START_DATE)}
          orientation='verticalScrollable'
          verticalHeight={800}
          numberOfMonths={4}
          onOutsideClick={handleOutsideClick}
          phrases={{
            ...DateRangePickerPhrases,
            focusStartDate: 'Interact with the calendar and add the arrival date date for your rental.',
            chooseAvailableStartDate: data => `Choose ${data.date} as your arrival date. It’s available.`,
            chooseAvailableEndDate: data => `Choose ${data.date} as your return date. It’s available.`
          }}
          isDayBlocked={isDayBlocked}
        />
      </div>
      {/* fields with correct names to post to checkout controller */}
      <input type='hidden' name='cart[timeline][arrival_date]' value={getInputValue(START_DATE, 'serverDateString')} />
      <input type='hidden' name='cart[timeline][return_date]' value={getInputValue(END_DATE, 'serverDateString')} />
    </div>
  )
}

DatePickerRenderer.propTypes = {
  focusedInput: PropTypes.oneOf([START_DATE, END_DATE]),
  setFocusedInput: PropTypes.func.isRequired,
  serverDates: PropTypes.object,
  clientDates: PropTypes.object,
  inputFunctions: PropTypes.shape({
    onClick: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired
  }).isRequired,
  showCalendar: PropTypes.bool.isRequired,
  handleOutsideClick: PropTypes.func.isRequired,
  onDatesChange: PropTypes.func.isRequired
}

export { DatePickerManager, DatePickerRenderer }
export default DatePickerManager
